import React, { useState, useEffect } from 'react';
import { MapPin, Phone, User, FileText, Truck, X, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { validatePhone } from '@/utils/mobileValidation';
import UkPostcodeLookup from '@/components/common/UkPostcodeLookup';

import { getCurrencySymbol } from '@/utils/currency';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';

export interface DeliveryDetails {
  address: string;
  postal_code: string;
  recipient_name?: string;
  phone?: string;
  notes?: string;
  delivery_charge?: number;
}

interface DeliveryDetailsModalProps {
  open: boolean;
  initialData?: DeliveryDetails;
  customerName?: string;
  customerPhone?: string;
  customerAddress?: string;
  customerPostalCode?: string;
  isWalkIn?: boolean;
  askDeliveryPrice?: boolean;
  onClose: () => void;
  onSave: (data: DeliveryDetails) => void;
}

/** Standard UK Postcode regex */
export const UK_POSTCODE_REGEX = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i;

/** Format UK Postcode to uppercase with standard space (e.g. SW1A 1AA) */
export function formatUkPostcode(val: string): string {
  const clean = val.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (clean.length > 3) {
    return `${clean.slice(0, clean.length - 3)} ${clean.slice(clean.length - 3)}`;
  }
  return clean;
}

export default function DeliveryDetailsModal({
  open,
  initialData,
  customerName = '',
  customerPhone = '',
  customerAddress = '',
  customerPostalCode = '',
  isWalkIn = false,
  askDeliveryPrice = false,
  onClose,
  onSave,
}: DeliveryDetailsModalProps) {
  useEscapeToClose(onClose, open);
  const { t } = useTranslation();
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [deliveryCharge, setDeliveryCharge] = useState('0');

  const [generalError, setGeneralError] = useState('');
  const [addressError, setAddressError] = useState('');
  const [postalCodeError, setPostalCodeError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const [postcodeVerifiedInfo, setPostcodeVerifiedInfo] = useState('');

  useEffect(() => {
    if (open) {
      setAddress(initialData?.address || customerAddress || '');
      setPostalCode(initialData?.postal_code || customerPostalCode || '');
      setRecipientName(initialData?.recipient_name || customerName || '');
      setPhone(initialData?.phone || customerPhone || '');
      setNotes(initialData?.notes || '');
      setDeliveryCharge(
        initialData?.delivery_charge !== undefined && initialData?.delivery_charge !== null
          ? String(initialData.delivery_charge)
          : '0'
      );
      setGeneralError('');
      setAddressError('');
      setPostalCodeError('');
      setPhoneError('');
      setPostcodeVerifiedInfo('');
    }
  }, [open, initialData, customerName, customerPhone, customerAddress, customerPostalCode]);

  if (!open) return null;

  const validateInputs = (): boolean => {
    let isValid = true;
    setGeneralError('');

    // 1. Delivery Address Check
    if (!address.trim()) {
      setAddressError(t('pos.addressRequired', 'Delivery address is required'));
      isValid = false;
    } else {
      setAddressError('');
    }

    // 2. Postal / ZIP Code Check (Mandatory UK Postcode)
    const cleanPostal = postalCode.trim();
    if (!cleanPostal) {
      setPostalCodeError(t('pos.postcodeRequired', 'Postal / ZIP code is required'));
      isValid = false;
    } else if (!UK_POSTCODE_REGEX.test(cleanPostal)) {
      setPostalCodeError(
        t('pos.invalidUkPostcode', 'Enter a valid UK postcode (e.g. SW1A 1AA or M1 1AE)')
      );
      isValid = false;
    } else {
      setPostalCodeError('');
    }

    // 3. Contact Phone Check (Mandatory UK Phone format)
    const cleanPhone = phone.trim();
    if (!cleanPhone) {
      setPhoneError(t('pos.phoneRequired', 'Contact phone number is required for delivery'));
      isValid = false;
    } else {
      const phoneValidationErr = validatePhone(cleanPhone, 'GB', { required: true });
      if (phoneValidationErr) {
        setPhoneError(phoneValidationErr);
        isValid = false;
      } else {
        setPhoneError('');
      }
    }

    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateInputs()) {
      setGeneralError(t('pos.fixFormErrors', 'Please correct the highlighted fields below.'));
      return;
    }

    onSave({
      address: address.trim(),
      postal_code: formatUkPostcode(postalCode.trim()),
      recipient_name: recipientName.trim() || undefined,
      phone: phone.trim(),
      notes: notes.trim() || undefined,
      delivery_charge: askDeliveryPrice ? Math.max(0, parseFloat(deliveryCharge) || 0) : undefined,
    });
    onClose();
  };

  const isPhoneValid = phone.trim() ? !validatePhone(phone.trim(), 'GB', { required: true }) : false;
  const isPostcodeValid = postalCode.trim() ? UK_POSTCODE_REGEX.test(postalCode.trim()) : false;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100 transform transition-all"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delivery-modal-title"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur-md">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 id="delivery-modal-title" className="text-lg font-bold">
                {t('pos.deliveryDetails', 'Delivery Information')}
              </h2>
              <p className="text-xs text-blue-100">
                {isWalkIn
                  ? t('pos.walkInDeliveryNote', 'Provide UK delivery destination for walk-in order')
                  : t('pos.deliveryAddressNote', 'Specify UK delivery address and recipient details')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {generalError && (
            <div className="flex items-center gap-2 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl animate-in fade-in">
              <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
              <span>{generalError}</span>
            </div>
          )}

          {/* Customer Address Status Banner */}
          {!isWalkIn && customerName && (
            <div className={`flex items-center gap-2 p-2.5 rounded-xl text-xs border ${
              address.trim() && postalCode.trim()
                ? 'bg-emerald-50/70 border-emerald-200 text-emerald-800'
                : 'bg-amber-50/70 border-amber-200 text-amber-800'
            }`}>
              {address.trim() && postalCode.trim() ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Delivery address loaded for <strong>{customerName}</strong>. You may review or update below.</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                  <span>No saved address found on file for <strong>{customerName}</strong>. Please fill in the details below.</span>
                </>
              )}
            </div>
          )}

          {/* Runtime Delivery Charge */}
          {askDeliveryPrice && (
            <div className="p-3 bg-amber-50/70 border border-amber-200/90 rounded-xl space-y-1.5 animate-in fade-in duration-150">
              <label className="block text-xs font-bold text-amber-950 uppercase tracking-wider flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <Truck className="w-3.5 h-3.5 text-amber-700" />
                  {t('pos.deliveryFee', 'Delivery Price / Fee')} ({getCurrencySymbol()})
                </span>
                <span className="text-[10px] font-semibold bg-amber-200/70 text-amber-900 px-2 py-0.5 rounded-full normal-case">
                  Runtime Order Fee
                </span>
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-bold text-gray-500 text-sm">
                  {getCurrencySymbol()}
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={deliveryCharge}
                  onChange={(e) => setDeliveryCharge(e.target.value)}
                  onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                  placeholder="0.00"
                  className="w-full pl-8 pr-3.5 py-2 text-sm font-semibold bg-white border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all text-gray-900"
                />
              </div>
              <p className="text-[11px] text-amber-800">
                This delivery charge will be added to the invoice total for this order.
              </p>
            </div>
          )}

          {/* Delivery Address */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-blue-600" />
              {t('pos.deliveryAddress', 'Delivery Address')} <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              rows={3}
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                if (addressError) setAddressError('');
                if (generalError) setGeneralError('');
              }}
              placeholder={t('pos.addressPlaceholder', 'House/Flat number, Street name, Area, Building or landmark...')}
              className={`w-full px-3.5 py-2.5 text-sm bg-gray-50 border rounded-xl focus:bg-white focus:outline-none focus:ring-2 transition-all placeholder:text-gray-400 ${
                addressError
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20 bg-red-50/30'
                  : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500/20'
              }`}
            />
            {addressError && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {addressError}
              </p>
            )}
          </div>

          {/* Postal Code & Contact Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {/* Postal Code (Mandatory UK) */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-blue-600" />
                  {t('pos.postalCode', 'Postal / ZIP Code')} <span className="text-red-500">*</span>
                </label>
                {isPostcodeValid && (
                  <span className="text-[11px] text-emerald-600 flex items-center gap-0.5 font-medium">
                    <CheckCircle2 className="w-3 h-3" /> Valid UK
                  </span>
                )}
              </div>
              <UkPostcodeLookup
                postalCode={postalCode}
                onPostalCodeChange={(v) => {
                  setPostalCode(v);
                  if (postalCodeError) setPostalCodeError('');
                  if (generalError) setGeneralError('');
                  setPostcodeVerifiedInfo('');
                }}
                onFill={(fields) => {
                  setPostalCode(fields.postal_code);
                  setPostalCodeError('');
                  if (fields.address) {
                    setAddress(fields.address);
                    if (addressError) setAddressError('');
                  } else if (!address.trim() && fields.city) {
                    setAddress(fields.city);
                  }
                  const locText = [fields.city, fields.province].filter(Boolean).join(', ');
                  setPostcodeVerifiedInfo(locText || 'Valid UK Postcode');
                }}
                inputClassName={`w-full pl-3.5 pr-3 py-2 text-sm uppercase bg-gray-50 border rounded-xl focus:bg-white focus:outline-none focus:ring-2 transition-all placeholder:text-gray-400 font-mono ${
                  postalCodeError
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20 bg-red-50/30'
                    : isPostcodeValid
                    ? 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500/20'
                    : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500/20'
                }`}
              />

              {postcodeVerifiedInfo && (
                <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 shrink-0" />
                  <span>{postcodeVerifiedInfo}</span>
                </p>
              )}
              {postalCodeError ? (
                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>{postalCodeError}</span>
                </p>
              ) : (
                <p className="text-[11px] text-gray-500 mt-1">Required UK postcode (e.g. SW1A 1AA)</p>
              )}
            </div>

            {/* Contact Phone (Mandatory UK format) */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-blue-600" />
                  {t('pos.recipientPhone', 'Contact Phone')} <span className="text-red-500">*</span>
                </label>
                {isPhoneValid && (
                  <span className="text-[11px] text-emerald-600 flex items-center gap-0.5 font-medium">
                    <CheckCircle2 className="w-3 h-3" /> Valid UK
                  </span>
                )}
              </div>
              <input
                type="text"
                required
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  if (phoneError) setPhoneError('');
                  if (generalError) setGeneralError('');
                }}
                placeholder="e.g. 07123 456789"
                className={`w-full px-3.5 py-2 text-sm bg-gray-50 border rounded-xl focus:bg-white focus:outline-none focus:ring-2 transition-all placeholder:text-gray-400 font-mono ${
                  phoneError
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20 bg-red-50/30'
                    : isPhoneValid
                    ? 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500/20'
                    : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500/20'
                }`}
              />
              {phoneError ? (
                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>{phoneError}</span>
                </p>
              ) : (
                <p className="text-[11px] text-gray-500 mt-1">UK format: 07xxx xxxxxx or +44 7xxx</p>
              )}
            </div>
          </div>

          {/* Recipient Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-gray-400" />
              {t('pos.recipientName', 'Recipient / Customer Name')}
            </label>
            <input
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="Name of person receiving the order"
              className="w-full px-3.5 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
            />
          </div>

          {/* Delivery Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-gray-400" />
              {t('pos.deliveryNotes', 'Delivery Instructions / Notes')}
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('pos.notesPlaceholder', 'e.g. Ring the bell twice, leave at reception or door...')}
              className="w-full px-3.5 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
            />
          </div>

          {/* Footer Buttons */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl shadow-sm shadow-blue-500/30 transition-all flex items-center gap-1.5"
            >
              <Truck className="w-4 h-4" />
              {t('pos.confirmDelivery', 'Set Delivery Details')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
